import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  Lock,
  CloudRain,
  Plane,
  Building2,
  Waves,
  Key
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AluminiumPage = () => {
  const features = [
    { text: 'For External Use', icon: CloudRain },
    { text: 'Built To Withstand Harsh UK Weather', icon: CloudRain },
    { text: 'Internal Installation with Locks & Keys', icon: Key },
    { text: 'T6 Aircraft Grade Aluminium', icon: Plane },
    { text: 'Vertical or Horizontal Louvres', icon: CheckCircle },
    { text: 'Powder Coated Finish', icon: Shield },
    { text: 'Low Maintenance', icon: CheckCircle },
    { text: 'Maximum Security', icon: Lock },
    { text: 'Corrosion Resistant', icon: Shield },
    { text: '10 Year Structural Warranty', icon: Shield }
  ];

  const bestFor = [
    'External windows',
    'Security applications',
    'Commercial premises',
    'Coastal properties',
    'Ground floor security',
    'Holiday homes'
  ];

  const faqs = [
    {
      question: 'Can Aluminium Shutters be used externally?',
      answer: 'Yes, our Aluminium Shutters are specifically designed for external use. Built from T6 Aircraft Grade Aluminium with a powder coated finish, they are engineered to withstand the harshest UK weather conditions including rain, wind, and coastal salt spray.'
    },
    {
      question: 'How do Aluminium Shutters provide security?',
      answer: 'Aluminium Shutters can be installed internally with integrated locks and keys, providing an additional layer of home security. The T6 Aircraft Grade Aluminium construction makes them extremely robust and difficult to breach, offering peace of mind for homeowners concerned about security.'
    },
    {
      question: 'Are Aluminium Shutters suitable for coastal properties?',
      answer: 'Absolutely. The powder coated finish and corrosion-resistant properties of Aircraft Grade Aluminium make these shutters ideal for coastal locations where salt spray and harsh weather can damage other materials.'
    },
    {
      question: 'What louvre options are available?',
      answer: 'Aluminium Shutters are available with either vertical or horizontal louvres, giving you flexibility in design and light control. Both options maintain the same high security and weather resistance standards.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20" style={{background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-200 mb-6">
                <Shield className="h-5 w-5 text-gray-700" />
                <Badge className="bg-gray-700 text-white">Security & External Range</Badge>
              </div>
              
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Aluminium Shutters
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Built from T6 Aircraft Grade Aluminium, designed for external use and to withstand harsh UK weather conditions. 
                Perfect for added home security with lock and key options.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Shield className="h-5 w-5 text-gray-700" />
                  <span className="font-semibold text-gray-900">10 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Lock className="h-5 w-5 text-gray-700" />
                  <span className="font-semibold text-gray-900">Maximum Security</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="bg-gray-800 hover:bg-gray-900 text-white px-8 py-4 text-lg">
                    <Calculator className="mr-2 h-5 w-5" />
                    Use Our Online Estimator
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-gray-800 text-gray-800 hover:bg-gray-100 px-8 py-4 text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Survey
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-gray-800 text-white px-4 py-2 rounded-full font-semibold shadow-lg z-10 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Security Grade
              </div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Aluminium Shutters"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Security & Weather Banner */}
      <section className="py-8 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex flex-col items-center gap-2">
              <Lock className="h-8 w-8" />
              <span className="font-semibold">Maximum Security</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <CloudRain className="h-8 w-8" />
              <span className="font-semibold">Weather Resistant</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Plane className="h-8 w-8" />
              <span className="font-semibold">Aircraft Grade</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8" />
              <span className="font-semibold">10 Year Warranty</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for Performance</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Aluminium Shutters combine robust security with weather-defying durability.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-gray-100">
                      <feature.icon className="h-6 w-6 text-gray-700" />
                    </div>
                    <span className="text-gray-700 font-medium pt-2">{feature.text}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Ideal Applications</h2>
              <p className="text-lg text-gray-600 mb-8">
                Aluminium Shutters excel where security and weather resistance are priorities. 
                From external installations to internal security applications, they deliver uncompromising protection.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {bestFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <Shield className="h-5 w-5 text-gray-700" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Aluminium Shutters External Installation"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Security & Durability</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Engineered for protection in every environment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Plane className="h-12 w-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aircraft Grade Material</h3>
                <p className="text-gray-600">T6 Aircraft Grade Aluminium provides exceptional strength and rigidity.</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Key className="h-12 w-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Lock & Key Security</h3>
                <p className="text-gray-600">Internal installations can include integrated locks for enhanced home security.</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Waves className="h-12 w-12 text-gray-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Coastal Ready</h3>
                <p className="text-gray-600">Powder coated finish resists salt spray and coastal corrosion.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our Aluminium Shutters
            </p>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Secure Your Property</h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant estimate for Aluminium Shutters using our online calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/materials">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Compare All Ranges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AluminiumPage;
